import { apiErrorMessage, CONNECTION_MESSAGE, retryAfterSeconds } from "@/domain/apiErrors";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  retryAfter?: number;

  constructor(message: string, status: number, retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

function withDefaultHeaders(headers?: HeadersInit): Headers {
  const result = new Headers({
    "Content-Type": "application/json",
  });

  if (headers) {
    new Headers(headers).forEach((value, key) => {
      result.set(key, value);
    });
  }

  return result;
}

export async function requestApi<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: "include",
      headers: withDefaultHeaders(init?.headers),
    });
  } catch (error) {
    if (init?.signal?.aborted) throw error;
    throw new ApiError(CONNECTION_MESSAGE, 0);
  }

  let json: ApiResponse<T> | null = null;
  try {
    json = (await response.json()) as ApiResponse<T>;
  } catch {
    json = null;
  }

  if (!response.ok || !json?.success) {
    throw new ApiError(apiErrorMessage(response.status, json), response.status, retryAfterSeconds(response.headers.get("Retry-After")));
  }

  return json.data as T;
}
